import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig, debounce } from '@theme/utilities';

/**
 * Cart rules for the SolVault catalogue, run on every page after each cart change.
 *
 * Rules come from the shop metafield `solvault.rules` (see snippets/sv-gifts.liquid), keyed by main
 * product id:
 *   { "<productId>": { "unit": [variantId, title], "once": [variantId, title], "tiers": [[qty, code], …] } }
 *
 * - `unit` gift: one per unit of the main product (summed across products sharing that gift), from
 *   `unit_min` units of that product (default 1);
 * - `once` gift: exactly one while any product that grants it is in the cart;
 * - `tiers`: bundle discount codes — the best tier reached by each product is applied to the cart,
 *   lower tiers are removed. Codes carry their own minimum quantity in Shopify, so they are safe.
 * Gift variants are $0 products; codes the shopper added themselves are left alone.
 *
 * @typedef {{ unit?: [number, string], unit_min?: number, once?: [number, string], tiers?: [number, string][] }} Rule
 * @typedef {{ key: string, product_id: number, variant_id: number, quantity: number }} CartLine
 */

const SOURCE = 'sv-gifts';

/** @type {Record<string, Rule> | undefined} */
const rules = /** @type {any} */ (window).svRules;

/** @type {Promise<void>} */
let queue = Promise.resolve();

/** @returns {string[]} */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId ?? '')
    .filter(Boolean);
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 */
async function post(url, body) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  return response.json();
}

/** @param {Record<string, Rule>} all */
function catalogue(all) {
  /** @type {Set<number>} */
  const giftVariants = new Set();
  /** @type {Set<string>} */
  const managedCodes = new Set();
  for (const rule of Object.values(all)) {
    if (rule.unit) giftVariants.add(rule.unit[0]);
    if (rule.once) giftVariants.add(rule.once[0]);
    for (const [, code] of rule.tiers ?? []) managedCodes.add(code.toUpperCase());
  }
  return { giftVariants, managedCodes };
}

/**
 * Brings gift lines and bundle codes in line with the cart. Returns the final cart response
 * (with rendered sections) when something changed, or null when the cart was already right.
 * @param {Record<string, Rule>} all
 */
async function reconcile(all) {
  const { giftVariants, managedCodes } = catalogue(all);

  /** @type {{ items: CartLine[], discount_codes?: { code: string }[] }} */
  const cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((r) => r.json());

  /** @type {Map<number, number>} units of each main product */
  const units = new Map();
  for (const line of cart.items) {
    if (all[String(line.product_id)]) units.set(line.product_id, (units.get(line.product_id) ?? 0) + line.quantity);
  }

  // Wanted quantity for every known gift variant (0 when nothing grants it).
  /** @type {Map<number, number>} */
  const wanted = new Map(Array.from(giftVariants, (id) => [id, 0]));
  /** @type {Set<string>} */
  const wantedCodes = new Set();
  for (const [productId, count] of units) {
    const rule = /** @type {Rule} */ (all[String(productId)]);
    // unit gift: one per unit, only once the product reaches `unit_min` units (default 1)
    if (rule.unit && count >= (rule.unit_min ?? 1)) wanted.set(rule.unit[0], (wanted.get(rule.unit[0]) ?? 0) + count);
    if (rule.once) wanted.set(rule.once[0], 1);
    const best = (rule.tiers ?? []).filter(([qty]) => count >= qty).sort((a, b) => b[0] - a[0])[0];
    if (best) wantedCodes.add(best[1].toUpperCase());
  }

  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {{ id: number, quantity: number, properties: Record<string, string> }[]} */
  const additions = [];
  for (const [variantId, qty] of wanted) {
    const lines = cart.items.filter((l) => l.variant_id === variantId);
    const have = lines.reduce((n, l) => n + l.quantity, 0);
    if (have === qty && lines.length <= 1) continue;
    if (!lines.length) {
      if (qty > 0) additions.push({ id: variantId, quantity: qty, properties: { _gift: 'SolVault' } });
      continue;
    }
    lines.forEach((l, i) => (updates[l.key] = i === 0 ? qty : 0));
  }

  const currentCodes = (cart.discount_codes ?? []).map((d) => d.code.toUpperCase());
  const keptCodes = currentCodes.filter((c) => !managedCodes.has(c));
  const nextCodes = [...keptCodes, ...wantedCodes];
  const codesChanged =
    nextCodes.length !== currentCodes.length || nextCodes.some((c) => !currentCodes.includes(c));

  if (!additions.length && !Object.keys(updates).length && !codesChanged) return null;

  if (additions.length) await post(Theme.routes.cart_add_url, { items: additions });
  // Finish with one update call: it applies quantities + codes and returns the rendered sections.
  /** @type {Record<string, unknown>} */
  const body = { updates, sections: cartSectionIds().join(','), sections_url: window.location.pathname };
  if (codesChanged) body.discount = nextCodes.join(',');
  return post(`${Theme.routes.cart_update_url}.js`, body);
}

/** @param {Record<string, Rule>} all */
function sync(all) {
  queue = queue
    .then(async () => {
      const deferred = CartLinesUpdateEvent.createPromise();
      const result = await reconcile(all);
      if (!result) return;

      document.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          items: result.items,
          itemCount: result.item_count,
          sections: result.sections ?? undefined,
          source: SOURCE,
          didError: false,
        },
      });
    })
    .catch((error) => console.warn('[sv-gifts]', error));
}

if (rules && Object.keys(rules).length && !(/** @type {any} */ (window).svGiftsReady)) {
  /** @type {any} */ (window).svGiftsReady = true;
  const run = debounce(() => sync(rules), 250);

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const { promise } = /** @type {CartLinesUpdateEvent} */ (event);
    if (!promise) return run();
    promise
      .then((/** @type {{ detail?: { source?: string } }} */ result) => {
        if (result?.detail?.source !== SOURCE) run();
      })
      .catch(() => {});
  });

  run();
}
